import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Row from "react-bootstrap/Row";
import toast from "react-hot-toast";
import accountsApi from "../../services/accountsCommonService";
import AccountsConstants from "../../common/AccountsConstants";
import { formatINR } from "./accountUser";
import { Field, controlClass, selectClass } from "./FormFields";
import { validateLedger } from "./validate";
import { EditIcon } from "./AccountIcons";
import { ApiAlert, FieldHint, FormDialog } from "./AccountWidgets";
import { getFieldErrors } from "./accountErrors";
import PageShell from "./PageShell";
import useAccountMeta from "./useAccountMeta";
import "./accounts.css";

export function LedgerList() {
  const { meta, error: metaError, labelOf } = useAccountMeta();
  const [rows, setRows] = useState([]);
  const [group, setGroup] = useState("");
  const [search, setSearch] = useState("");
  const [isActive, setIsActive] = useState("");
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = async (event) => {
    if (event) event.preventDefault();
    setError("");
    try {
      const result = await accountsApi.ledgers({ group, search, isActive });
      setRows(result?.data || []);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageShell title="Ledger Master" subtitle="Chart of accounts" actions={<button className="btn btn-primary" type="button" onClick={() => setDialog({})}>New ledger</button>}>
      <ApiAlert error={error || metaError} />
      <form className="accounts-form" onSubmit={load}>
        <div className="accounts-filter-bar">
          <label className="tally-field">Search by name
            <input className="form-control" value={search} placeholder="Type a ledger name" onChange={(event) => setSearch(event.target.value)} />
          </label>
          <label className="tally-field">Group
            <select className="form-select" value={group} onChange={(event) => setGroup(event.target.value)}>
              <option value="">All groups</option>
              {(meta?.ledgerGroups || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="tally-field">Status
            <select className="form-select" value={isActive} onChange={(event) => setIsActive(event.target.value)}>
              <option value="">All</option>
              {(meta?.ledgerStatuses || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <div className="accounts-toolbar">
            <button className="btn btn-outline-secondary" type="submit">Search</button>
          </div>
        </div>
      </form>
      <div className="accounts-table-wrap">
        <table className="accounts-table">
          <thead>
            <tr><th>Name</th><th>Code</th><th>Group</th><th className="num">Opening balance</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._id}>
                <td>{row.name}</td>
                <td>{row.code}</td>
                <td>{labelOf("ledgerGroups", row.group)}</td>
                <td className="num">{formatINR(row.openingBalance)} {row.openingBalanceType === "CR" ? "Cr" : "Dr"}</td>
                <td><span className={`accounts-chip ${row.isActive ? "ok" : ""}`}>{row.isActive ? "Active" : "Inactive"}</span></td>
                <td><EditIcon onClick={() => setDialog(row)} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6}>No ledgers match these filters. Add one to start posting vouchers.</td></tr>}
          </tbody>
        </table>
      </div>
      {dialog && <LedgerDialog ledger={dialog._id ? dialog : null} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </PageShell>
  );
}

function LedgerDialog({ ledger, onClose, onSaved }) {
  const { meta } = useAccountMeta();
  const [form, setForm] = useState({
    name: ledger?.name || "",
    code: ledger?.code || "",
    group: ledger?.group || "",
    openingBalance: ledger ? String(ledger.openingBalance) : "0",
    openingBalanceType: ledger?.openingBalanceType || "DR",
    isActive: ledger ? ledger.isActive !== false : true,
  });
  const [error, setError] = useState(null);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const groups = (meta?.ledgerGroups || []).filter((item) => item.value !== "SALES" && item.value !== "PURCHASE");
  const balanceOk = /^\d+(\.\d{1,2})?$/.test(String(form.openingBalance).trim() || "0");
  const local = {
    name: !form.name.trim() ? "Ledger name is required" : "",
    group: !form.group ? "Group is required" : "",
    openingBalance: !balanceOk ? "Enter an amount with at most 2 decimals" : "",
  };
  const server = getFieldErrors(error);
  const show = (key) => (tried ? local[key] : "") || server[key] || "";

  const save = async (event) => {
    event.preventDefault();
    setTried(true);
    setError(null);
    if (local.name || local.group || local.openingBalance) return;
    setSaving(true);
    try {
      const result = await accountsApi.saveLedger({
        name: form.name.trim(),
        code: form.code.trim(),
        group: form.group,
        openingBalance: String(form.openingBalance).trim() || "0",
        openingBalanceType: form.openingBalanceType,
        isActive: form.isActive,
      }, ledger?._id);
      toast.success(result?.message || "Ledger saved", { position: "top-right" });
      onSaved();
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };

  return (
    <FormDialog
      title={ledger ? "Edit ledger" : "New ledger"}
      onClose={onClose}
      onSubmit={save}
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save ledger"}</button>
        </>
      )}
    >
      <ApiAlert error={error} />
      <div className="accounts-form-grid">
        <label className="tally-field span-2">Ledger name *
          <input className={`form-control ${show("name") ? "is-invalid" : ""}`} value={form.name} placeholder="Ledger name" onChange={(event) => setForm({ ...form, name: event.target.value })} autoFocus />
          <FieldHint error={show("name")} />
        </label>
        <label className="tally-field">Code
          <input className="form-control" value={form.code} placeholder="Short code" onChange={(event) => setForm({ ...form, code: event.target.value })} />
          <FieldHint />
        </label>
        <label className="tally-field">Group *
          <select className={`form-select ${show("group") ? "is-invalid" : ""}`} value={form.group} onChange={(event) => setForm({ ...form, group: event.target.value })}>
            <option value="">Select</option>
            {groups.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <FieldHint error={show("group")} />
        </label>
        <label className="tally-field">Opening balance (₹)
          <input className={`form-control ${show("openingBalance") ? "is-invalid" : ""}`} value={form.openingBalance} placeholder="0.00" onChange={(event) => setForm({ ...form, openingBalance: event.target.value })} />
          <FieldHint error={show("openingBalance")} />
        </label>
        <label className="tally-field">Dr / Cr
          <select className="form-select" value={form.openingBalanceType} onChange={(event) => setForm({ ...form, openingBalanceType: event.target.value })}>
            {(meta?.balanceTypes || [{ value: "DR", label: "Dr" }, { value: "CR", label: "Cr" }]).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <FieldHint />
        </label>
        <label className="accounts-check span-2">
          <input type="checkbox" checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
          <span>Active (available for voucher entry)</span>
        </label>
      </div>
    </FormDialog>
  );
}
const emptyLedger = {
  name: "",
  code: "",
  group: "SUNDRY_DEBTOR",
  openingBalance: 0,
  openingBalanceType: "DR",
  isActive: true,
  assignedSalesmanId: "",
  customerUserId: "",
  contact: { phone: "", email: "", address: "" },
  bankDetails: { accountNo: "", ifsc: "", branch: "" },
};

export function LedgerForm() {
  const navigate = useNavigate();
  const { meta } = useAccountMeta();
  const [searchParams] = useSearchParams();
  const ledgerId = searchParams.get("id");
  const [form, setForm] = useState(emptyLedger);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    accountsApi.lookups()
      .then((result) => setUsers(result?.data?.users || []))
      .catch((err) => toast.error(err.message, { position: "top-right" }));
  }, []);

  useEffect(() => {
    if (!ledgerId) return;
    accountsApi.ledger(ledgerId).then((result) => {
      const row = result.data;
      setForm({
        ...emptyLedger,
        ...row,
        assignedSalesmanId: row.assignedSalesmanId || "",
        customerUserId: row.customerUserId || "",
        contact: { ...emptyLedger.contact, ...(row.contact || {}) },
        bankDetails: { ...emptyLedger.bankDetails, ...(row.bankDetails || {}) },
      });
    }).catch((err) => toast.error(err.message, { position: "top-right" }));
  }, [ledgerId]);

  const set = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const save = async (event) => {
    event.preventDefault();
    setError("");
    const nextErrors = validateLedger(form);
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    try {
      await accountsApi.saveLedger({
        ...form,
        assignedSalesmanId: form.assignedSalesmanId || null,
        customerUserId: form.customerUserId || null,
        openingBalance: Number(form.openingBalance || 0),
      }, ledgerId);
      navigate(AccountsConstants.LEDGER_LIST);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <PageShell title={ledgerId ? "Edit ledger" : "New ledger"} subtitle="Ledger master">
      <form className="accounts-form" onSubmit={save} noValidate>
      {error && <div className="accounts-error">{error}</div>}
      <Row>
        <Field label="Name" required error={fieldErrors.name}>
          <input className={controlClass(fieldErrors.name)} value={form.name} onChange={(event) => set("name", event.target.value)} />
        </Field>
        <Field label="Code">
          <input className="form-control" value={form.code} onChange={(event) => set("code", event.target.value)} />
        </Field>
        <Field label="Group" required error={fieldErrors.group}>
          <select className={selectClass(fieldErrors.group)} value={form.group} onChange={(event) => set("group", event.target.value)}>
              <option value="">Select</option>
              {(meta?.ledgerGroups || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </Field>
        <Field label="Opening balance" error={fieldErrors.openingBalance}>
          <input className={controlClass(fieldErrors.openingBalance)} type="number" min="0" step="0.01" value={form.openingBalance} onChange={(event) => set("openingBalance", event.target.value)} />
        </Field>
        <Field label="Dr / Cr">
          <select className="form-select" value={form.openingBalanceType} onChange={(event) => set("openingBalanceType", event.target.value)}>
            {(meta?.balanceTypes || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </Field>
        <Field label="Assigned salesman">
          <select className="form-select" value={form.assignedSalesmanId} onChange={(event) => set("assignedSalesmanId", event.target.value)}>
            <option value="">None</option>
            {users.map((user) => <option key={user._id} value={user._id}>{user.name} ({user.userId})</option>)}
          </select>
        </Field>
        <Field label="Customer login">
          <select className="form-select" value={form.customerUserId} onChange={(event) => set("customerUserId", event.target.value)}>
            <option value="">None</option>
            {users.map((user) => <option key={user._id} value={user._id}>{user.name} ({user.userId})</option>)}
          </select>
        </Field>
        <Field label="Phone">
          <input className="form-control" value={form.contact.phone} onChange={(event) => setForm((current) => ({ ...current, contact: { ...current.contact, phone: event.target.value } }))} />
        </Field>
        <Field label="Email" error={fieldErrors.email}>
          <input className={controlClass(fieldErrors.email)} value={form.contact.email} onChange={(event) => setForm((current) => ({ ...current, contact: { ...current.contact, email: event.target.value } }))} />
        </Field>
        <Field label="Address" md={12} lg={12}>
          <input className="form-control" value={form.contact.address} onChange={(event) => setForm((current) => ({ ...current, contact: { ...current.contact, address: event.target.value } }))} />
        </Field>
      </Row>
      {form.group === "BANK" && (
        <Row>
          <Field label="Account no" required error={fieldErrors.accountNo}>
            <input className={controlClass(fieldErrors.accountNo)} value={form.bankDetails.accountNo} onChange={(event) => setForm((current) => ({ ...current, bankDetails: { ...current.bankDetails, accountNo: event.target.value } }))} />
          </Field>
          <Field label="IFSC" required error={fieldErrors.ifsc}>
            <input className={controlClass(fieldErrors.ifsc)} value={form.bankDetails.ifsc} onChange={(event) => setForm((current) => ({ ...current, bankDetails: { ...current.bankDetails, ifsc: event.target.value } }))} />
          </Field>
          <Field label="Branch" required error={fieldErrors.branch}>
            <input className={controlClass(fieldErrors.branch)} value={form.bankDetails.branch} onChange={(event) => setForm((current) => ({ ...current, bankDetails: { ...current.bankDetails, branch: event.target.value } }))} />
          </Field>
        </Row>
      )}
      <div className="accounts-form-actions">
        <button className="btn btn-outline-secondary" type="button" onClick={() => navigate(AccountsConstants.LEDGER_LIST)}>Back</button>
        <button className="btn btn-primary" type="submit">Save</button>
      </div>
      </form>
    </PageShell>
  );
}
